// src/components/EquipmentTable.jsx

import React, { useState } from 'react';
import { Table, Button, DatePicker, Radio, Modal } from 'antd';
import dayjs from 'dayjs';

//import './EquipmentTable.css';

const { RangePicker } = DatePicker;

const EquipmentTable = ({
  filteredData,
  handleNameClick,
  showHistoryModal,
}) => {
  const [isQrModalOpen, setIsQrModalOpen] = useState(false);
  const [qrLink, setQrLink] = useState(null);

  // Функция для определения цвета даты (красный/жёлтый/зелёный)
  const getColorForDate = (date) => {
    if (!date) return 'black';
    const currentDate = dayjs();
    const targetDate = dayjs(date);
    const diffInDays = targetDate.diff(currentDate, 'day');

    if (diffInDays < 0) {
      return 'red';  // просрочено
    } else if (diffInDays < 15) {
      return 'orange'; // скоро
    } else {
      return 'green'; // далеко
    }
  };

  // Фильтрация дат
  const handleDateFilter = (value, record, dateField) => {
    const currentDate = dayjs();
    const targetDate = dayjs(record[dateField]);
    const thirtyDaysFromNow = currentDate.add(15, 'day');

    if (!record[dateField]) {
      // Если в поле нет даты, фильтр "past"/"upcoming" не будет совпадать
      return false;
    }

    // Если выбрана пара дат (RangePicker)
    if (Array.isArray(value)) {
      const [startDate, endDate] = value;
      return targetDate.isBetween(dayjs(startDate), dayjs(endDate), 'day', '[]');
    }

    // Иначе Radio-варианты
    if (value === 'past') {
      return targetDate.isBefore(currentDate);
    } else if (value === 'upcoming') {
      return targetDate.isAfter(currentDate);
    } else if (value === 'yellow') {
      // Даты в пределах 15 дней
      return targetDate.isAfter(currentDate) && targetDate.isBefore(thirtyDaysFromNow);
    }
    return false;
  };

  /**
   * Фабрика колонок с фильтром по датам (кастомная `filterDropdown`).
   * Проверяет "Н/У" в freqField (если freqField === 'Н/У', показываем "Н/У")
   */
  const getToFilters = (columnName, dateField, freqField) => ({
    title: columnName,
    key: dateField,
    className: 'custom-column',

    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div className="custom-filter-dropdown">
        <Radio.Group
          onChange={(e) => setSelectedKeys([e.target.value])}
          value={selectedKeys[0]}
          className="custom-radio-group"
        >
          <Radio value="past" className="custom-radio">Прош</Radio>
          <Radio value="yellow" className="custom-radio">Наст</Radio>
          <Radio value="upcoming" className="custom-radio">Буд</Radio>
        </Radio.Group>
        <RangePicker
          value={selectedKeys[1] || null}
          onChange={(dates) => setSelectedKeys([selectedKeys[0], dates])}
          className="custom-range-picker"
        />
        <div className="custom-filter-buttons">
          <Button
            onClick={clearFilters}
            size="small"
            className="custom-filter-reset-btn"
          >
            Сбросить
          </Button>
          <Button
            type="primary"
            onClick={() => confirm()}
            size="small"
            className="custom-filter-btn"
          >
            Применить
          </Button>
        </div>
      </div>
    ),

    onFilter: (value, record) => handleDateFilter(value, record, dateField),

    render: (_, record) => {
      const dateValue = record[dateField];
      const freqValue = record[freqField];

      // Если периодичность == 'Н/У'
      if (freqValue === 'Н/У') {
        return <span style={{ color: 'gray' }}>Н/У</span>;
      }

      // Если даты нет
      if (!dateValue) {
        return <span style={{ color: 'black' }}>—</span>;
      }

      // Иначе цветная дата
      const color = getColorForDate(dateValue);
      return <span style={{ color }}>{dateValue}</span>;
    },
  });

  // Определение цвета для каждого «подстатуса»
  const getSubStatusColor = (subStatus, record) => {
    // 'ОК'
    if (subStatus === 'ОК') {
      return 'green';
    }
    // Мануальные статусы
    if (['На ремонте', 'Неисправен', 'Ожидает зап части', 'Списан'].includes(subStatus)) {
      if (subStatus === 'Списан') {
        return 'gray';
      }
      return 'red';
    }
    // ТО1, ТО2, ...
    let dateStr = null;
    switch (subStatus) {
      case 'ТО1': dateStr = record.to1; break;
      case 'ТО2': dateStr = record.to2; break;
      case 'ТО3': dateStr = record.to3; break;
      case 'КР':  dateStr = record.majorRepair; break;
      case 'ПВ':  dateStr = record.verification; break;
      case 'КЛ':  dateStr = record.calibration; break;
      case 'ИСП': dateStr = record.testing; break;
      default:    dateStr = null;
    }
    if (!dateStr) return 'black';
    return getColorForDate(dateStr);
  };

  // Рендер статусной строки
  const renderStatus = (statusString, record) => {
    if (!statusString) {
      return <span style={{ color: 'black' }}>—</span>;
    }
    const splitted = statusString.split(',').map(s => s.trim());
    return (
      <div>
        {splitted.map((subStatus, index) => {
          const color = getSubStatusColor(subStatus, record);
          return (
            <span
              key={`${record.id}-${subStatus}-${index}`}
              style={{ color, fontWeight: 'bold', marginRight: '8px' }}
            >
              {subStatus}
            </span>
          );
        })}
      </div>
    );
  };

  // Столбцы
  const columns = [
    {
      title: '№',
      dataIndex: 'index',
      width: 50,
      align: 'center',
      render: (_, __, index) => index + 1,
    },
    {
      title: 'Наименование',
      dataIndex: 'name',
      key: 'name',
      align: 'left',  // <-- просим AntD выравнивать
      render: (_, record) => (
        <button
          onClick={() => handleNameClick(record)}
          style={{
            background: 'none',
            border: 'none',
            textAlign: 'left',
            padding: 0,
            color: '#1890ff',
            textDecoration: 'underline',  
            cursor: 'pointer'
          }}
        >
          {record.name}
        </button>
      ),
    },
    {
      title: 'Производитель',
      dataIndex: 'manufacturer',
      key: 'manufacturer',
      className: 'custom-column',
    },
    {
      title: 'Серийный номер',
      dataIndex: 'serialNumber',
      key: 'serialNumber',
      className: 'custom-column',
    },
    {
      title: 'Инструкции',
      key: 'instructions',
      className: 'custom-column',
      render: (_, record) => (
        <>
          <button
            onClick={() => {
              setQrLink(record.qrLink);
              setIsQrModalOpen(true);
            }}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              color: '#1890ff',
              textDecoration: 'underline',
              cursor: 'pointer'
            }}
          >
            QR-код
          </button>
          {' | '}
          <a href={record.manualLink} target="_blank" rel="noopener noreferrer">
            Офиц. мануал
          </a>
        </>
      ),
    },
    {
      title: 'Локация',
      dataIndex: 'location',
      key: 'location',
      className: 'custom-column',
      filters: [
        { text: 'Завод Таптатты', value: 'Завод Таптатты' },
        { text: 'Завод Ондырыс', value: 'Завод Ондырыс' },
        { text: 'Магазин Таптатты', value: 'Магазин Таптатты' },
      ],
      onFilter: (value, record) => record.location === value,
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      className: 'custom-column',
      filters: [
        { text: 'ОК', value: 'ОК' },
        { text: 'ТО1', value: 'ТО1' },
        { text: 'ТО2', value: 'ТО2' },
        { text: 'ТО3', value: 'ТО3' },
        { text: 'КР', value: 'КР' },
        { text: 'ПВ', value: 'ПВ' },
        { text: 'КЛ', value: 'КЛ' },
        { text: 'ИСП', value: 'ИСП' },
        { text: 'На ремонте', value: 'На ремонте' },
        { text: 'Неисправен', value: 'Неисправен' },
        { text: 'Ожидает зап части', value: 'Ожидает зап части' },
        { text: 'Списан', value: 'Списан' },
      ],
      onFilter: (value, record) => {
        if (!record.status) return false;
        const statuses = record.status.split(',').map(s => s.trim());
        return statuses.includes(value);
      },
      render: (text, record) => renderStatus(text, record),
    },

    // Дата-колонки с кастомным filterDropdown
    getToFilters('ТО 1', 'to1', 'to1Frequency'),
    getToFilters('ТО 2', 'to2', 'to2Frequency'),
    getToFilters('ТО 3', 'to3', 'to3Frequency'),
    getToFilters('Кап ремонт', 'majorRepair', 'majorRepairFrequency'),
    getToFilters('Поверка', 'verification', 'verificationFrequency'),
    getToFilters('Калибровка', 'calibration', 'calibrationFrequency'),
    getToFilters('Испытания', 'testing', 'testingFrequency'),

    {
      title: 'История изменений',
      key: 'history',
      className: 'custom-column',
      render: (_, record) => (
        <Button onClick={() => showHistoryModal(record.id)}>Посмотреть историю</Button>
      ),
    },
  ];

  return (
    <>
      <Table
        dataSource={filteredData.map((record) => ({
          ...record,
          key: record.id,
        }))}
        columns={columns}
        pagination={false}
        className="custom-table"
        /* 
         * ВАЖНО: Добавляем locale, чтобы заменить "OK"/"Reset" 
         * на «Применить» / «Сбросить»:
         */
        locale={{
          filterConfirm: 'Применить',
          filterReset: 'Сбросить',
        }}
      />

      <Modal
        title="QR-код"
        open={isQrModalOpen}
        onCancel={() => setIsQrModalOpen(false)}
        footer={null}
      >
        {qrLink ? (
          <img src={qrLink} alt="QR-код" style={{ width: '100%' }} />
        ) : (
          <p>QR-код отсутствует</p>
        )}
      </Modal>
    </>
  );
};

export default EquipmentTable;
